import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Send, X, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = ({ tourId, tourTitle, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: tourTitle ? `Question about: ${tourTitle}` : '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        tour_id: tourId || null
      });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="contact-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-8 h-8 text-white" />
              <div>
                <h2 className="text-xl font-bold text-white">Contact Us</h2>
                <p className="text-cyan-100 text-sm">We'd love to hear from you!</p>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="close-contact-modal"
              className="text-white/80 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-all"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Direct email option */}
        <div className="px-6 pt-4 pb-2 bg-cyan-50 border-b border-cyan-100">
          <p className="text-sm text-gray-600 text-center">
            Fill out the form below, or email us directly at{' '}
            <a 
              href="mailto:contact@audiostride.com" 
              className="font-semibold text-cyan-600 hover:text-cyan-700 underline"
            >
              contact@audiostride.com
            </a>
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              data-testid="contact-name-input"
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Your Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              data-testid="contact-email-input"
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="What's this about?"
              data-testid="contact-subject-input"
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Your Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us what's on your mind..."
              rows={5}
              data-testid="contact-message-input"
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all resize-none"
            />
          </div>
          
          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="send-contact-button"
              className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300 text-white py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2"
            >
              <Send className="w-5 h-5" />
              {submitting ? 'Sending...' : 'Send Message'}
            </button>
          </div>
          
          <p className="text-xs text-gray-500 text-center">
            Your email will only be used to respond to your inquiry.
          </p>
        </form>
      </div>
    </div>
  );
};

// Contact button that opens the modal
const ContactButton = ({ tourId, tourTitle, variant = 'default' }) => {
  const [showModal, setShowModal] = useState(false);
  
  const buttonClasses = {
    default: 'bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2',
    outline: 'border-2 border-gray-300 hover:border-cyan-500 hover:text-cyan-600 text-gray-600 px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2',
    text: 'text-cyan-600 hover:text-cyan-700 font-semibold flex items-center gap-1 transition-all'
  };
  
  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        data-testid="open-contact-button"
        className={buttonClasses[variant]}
      >
        <MessageCircle className="w-4 h-4" />
        Contact Us
      </button>
      
      {showModal && (
        <ContactForm
          tourId={tourId}
          tourTitle={tourTitle}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export { ContactForm, ContactButton };
export default ContactButton;
